import React, { useState, useEffect, useCallback } from 'react';
import Head from 'next/head';
import CalculatorQuoteRequest from "@/components/tools/CalculatorQuoteRequest";
import { motion } from 'framer-motion';
import { 
  Calendar, 
  AlertTriangle, 
  CheckCircle, 
  Clock, 
  Info, 
  Search,
  Shield,
  Target,
  Gauge,
  Car,
  TrendingUp,
  Camera,
  Smartphone,
  X,
  Download,
  Play
} from 'lucide-react';
import dynamic from 'next/dynamic';
import { usePopup } from '@/contexts/PopupContext';
import DownloadApp from '@/components/shared/DownloadApp';

const RequestItemFormPopup = dynamic(() => import("@/components/requestItem/RequestItemFormPopup"), {
  ssr: false,
});

interface TyreExpiryResult {
  dateCode: string;
  weekNumber: number;
  year: number;
  manufacturingDate: Date;
  expiryDate: Date;
  isExpired: boolean;
  daysToExpiry: number;
  yearsToExpiry: number;
  monthsToExpiry: number;
  isValid: boolean;
  errorMessage?: string;
}

const TyreExpiryDateCalculator: React.FC = () => {
  const [dateCode, setDateCode] = useState<string>('');
  const [result, setResult] = useState<TyreExpiryResult | null>(null);
  const [showInfo, setShowInfo] = useState(false);

  // Use the popup context - replace the existing usePopup destructuring:
  const { 
    isPopupOpen, 
    popupType, 
    resetPopup,
    setPopupOpen,
    setPopupType
  } = usePopup();

  // Add local state as backup
  const [localShowPopup, setLocalShowPopup] = useState(false);

  // Replace the existing handleTyreQuoteRequest function with this corrected version:
  const handleTyreQuoteRequest = () => {
    try {
      console.log('=== TYRE QUOTE REQUEST CLICKED ===');
      
      // Set initial data for the tyre service request
      const serviceData = {
        title: 'Tyre Replacement & Quote Service',
        description: 'Get multiple quotes for new tyres based on your specifications. Snap a pic of your tyre and obtain quotes from verified suppliers.',
        serviceId: 'tyre-replacement',
        categoryId: 'tyres',
        subCategoryId: 'replacement',
        domainCategoryId: 'automotive'
      };

      // Store in sessionStorage like other working pages
      if (typeof window !== 'undefined') {
        sessionStorage.setItem('selectedServiceData', JSON.stringify(serviceData));
      }

      // Try available methods to open popup
      if (typeof setPopupOpen === 'function' && typeof setPopupType === 'function') {
        console.log('Using context methods: setPopupType + setPopupOpen');
        setPopupType('carousel');
        setPopupOpen(true);
      } else {
        console.log('Using local state fallback');
        setLocalShowPopup(true);
      }
      
    } catch (error) {
      console.error('Error in handleTyreQuoteRequest:', error);
    }
  };

  // Add popup close handler
  const handleClosePopup = () => {
    try {
      console.log('=== CLOSING POPUP ===');
      
      // Use resetPopup if available
      if (typeof resetPopup === 'function') {
        resetPopup();
      }
      
      // Or use setPopupOpen if available
      if (typeof setPopupOpen === 'function') {
        setPopupOpen(false);
      }
      
      // Also close local state
      setLocalShowPopup(false);
      
      // Clear session data
      if (typeof window !== 'undefined') {
        sessionStorage.removeItem('selectedServiceData');
      }
    } catch (error) {
      console.error('Error in handleClosePopup:', error);
    }
  };

  // Add getInitialData function
  const getInitialData = useCallback(() => {
    if (typeof window !== 'undefined') {
      const storedData = sessionStorage.getItem('selectedServiceData');
      if (storedData) {
        try {
          return JSON.parse(storedData);
        } catch (e) {
          console.error('Error parsing stored service data:', e);
        }
      }
    }
    return undefined;
  }, []);

  const calculateExpiryDate = (code: string): TyreExpiryResult | null => {
    // Remove any spaces and ensure 4 digits
    const cleanCode = code.replace(/\s/g, '');
    
    if (cleanCode.length !== 4 || !/^\d{4}$/.test(cleanCode)) {
      return {
        dateCode: code,
        weekNumber: 0,
        year: 0,
        manufacturingDate: new Date(),
        expiryDate: new Date(),
        isExpired: false,
        daysToExpiry: 0,
        yearsToExpiry: 0,
        monthsToExpiry: 0,
        isValid: false,
        errorMessage: 'Please enter a valid 4-digit DOT code (e.g., 1225)'
      };
    }

    const weekNumber = parseInt(cleanCode.substring(0, 2));
    const yearDigits = parseInt(cleanCode.substring(2, 4));
    
    // Validate week number
    if (weekNumber < 1 || weekNumber > 53) {
      return {
        dateCode: code,
        weekNumber,
        year: yearDigits,
        manufacturingDate: new Date(),
        expiryDate: new Date(),
        isExpired: false,
        daysToExpiry: 0,
        yearsToExpiry: 0,
        monthsToExpiry: 0,
        isValid: false,
        errorMessage: 'Week number must be between 01 and 53'
      };
    }

    // Convert 2-digit year to 4-digit year
    // Assume years 00-30 are 2000-2030, and 31-99 are 1931-1999
    const fullYear = yearDigits <= 30 ? 2000 + yearDigits : 1900 + yearDigits;
    
    // Calculate manufacturing date (first day of the week)
    const manufacturingDate = new Date(fullYear, 0, 1 + (weekNumber - 1) * 7);
    
    // Add 5 years for expiry date
    const expiryDate = new Date(manufacturingDate);
    expiryDate.setFullYear(expiryDate.getFullYear() + 5);
    
    const currentDate = new Date();
    const isExpired = currentDate > expiryDate;
    
    // Calculate time differences
    const timeDiff = expiryDate.getTime() - currentDate.getTime();
    const daysToExpiry = Math.ceil(timeDiff / (1000 * 3600 * 24));
    const yearsToExpiry = Math.floor(daysToExpiry / 365);
    const monthsToExpiry = Math.floor(daysToExpiry / 30);

    return {
      dateCode: cleanCode,
      weekNumber,
      year: fullYear,
      manufacturingDate,
      expiryDate,
      isExpired,
      daysToExpiry,
      yearsToExpiry,
      monthsToExpiry,
      isValid: true
    };
  };

  useEffect(() => {
    if (dateCode.length === 4) {
      const result = calculateExpiryDate(dateCode);
      setResult(result);
    } else {
      setResult(null);
    }
  }, [dateCode]);

  const formatDate = (date: Date): string => {
    return date.toLocaleDateString('en-GB', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatMonth = (date: Date): string => {
    return date.toLocaleDateString('en-GB', {
      year: 'numeric',
      month: 'long'
    });
  };

 

  const title = 'Tyre Expiry Date Calculator | Tyre Expiry and the Tyre Experts';
  const description = 'Road Safety And Aspects Of Tyre Safety Studies of tyre safety show that maintaining proper tyre pressure, observing tyre and vehicle load limits. Free tyre DOT code calculator to check if your tyres are expired.';
  const keywords = 'Tyre Expiry and the Tyre Experts, tyre expiry date calculator, DOT code calculator, tyre safety, road safety, tyre age, when to replace tyres, tyre expiry check, South Africa tyre safety';

  return (
    <>
      <Head>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta name="keywords" content={keywords} />
        <link rel="canonical" href="https://yoursite.com/online-automotive-calculators-and-tools/tyre-expiry-date-calculator" />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:image" content="/images/tyre-expiry-calculator-og.jpg" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content="/images/tyre-expiry-calculator-og.jpg" />
      </Head>

      <div className="min-h-screen bg-gradient-to-br from-yellow-50 to-red-50">
        {/* Header */}
        <div className="bg-gradient-to-r from-red-600 to-yellow-600 text-white py-16">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center max-w-4xl mx-auto"
            >
              <h1 className="text-4xl md:text-5xl font-bold mb-6 flex items-center justify-center gap-4">
                <Calendar className="text-yellow-400" size={50} />
                Tyre Expiry Date Calculator
              </h1>
              <p className="text-xl md:text-2xl mb-8 opacity-90">
                Check if your tyres are expired using the DOT code
              </p>
              <div className="flex flex-wrap justify-center gap-4 text-sm">
                <span className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full">
                  <Shield size={16} />
                  Road Safety First
                </span>
                <span className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full">
                  <Clock size={16} />
                  5 Year Expiry Rule
                </span>
                <span className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full">
                  <CheckCircle size={16} />
                  Instant Results
                </span>
              </div>
            </motion.div>
          </div>
        </div>

        <div className="container mx-auto px-4 py-12">
          {/* Snap a Pic Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-4xl mx-auto mb-12"
          >
            <div className="bg-gradient-to-r from-red-600 to-yellow-600 rounded-2xl p-8 text-white text-center">
              {/* ... keep existing content ... */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <button
                  onClick={handleTyreQuoteRequest}
                  className="bg-white text-blue-600 hover:bg-gray-100 px-8 py-4 rounded-lg font-bold text-lg transition-colors flex items-center gap-3"
                >
                  <Smartphone size={24} />
                  Request Tyre Quotes
                </button>
                <a
                  href="https://youtu.be/6dS5_nPw0X4"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-blue-600 px-8 py-4 rounded-lg font-bold text-lg transition-colors flex items-center gap-3"
                >
                  <Play size={24} />
                  Watch Demo
                </a>
              </div>
            </div>
          </motion.div>

          {/* Calculator Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-4xl mx-auto mb-12"
          >
            <div className="bg-white rounded-2xl p-8 shadow-xl">
              <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
                Enter Your Tyre DOT Code
              </h2>

              {/* Input Section */}
              <div className="max-w-md mx-auto mb-8">
                <div className="relative">
                  <input
                    type="text"
                    value={dateCode}
                    onChange={(e) => setDateCode(e.target.value.replace(/\D/g, '').slice(0, 4))}
                    placeholder="Enter 4-digit DOT code (e.g., 1225)"
                    className="w-full px-6 py-4 text-2xl font-bold text-center border-3 border-gray-300 rounded-xl focus:border-blue-500 focus:outline-none tracking-widest"
                    maxLength={4}
                  />
                  <div className="absolute -bottom-8 left-0 right-0 text-center text-sm text-gray-500">
                    Week (01-53) + Year (00-99)
                  </div>
                </div>
              </div>

              {/* Info Button */}
              <div className="text-center mb-8">
                <button
                  onClick={() => setShowInfo(!showInfo)}
                  className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-800 font-semibold"
                >
                  <Info size={16} />
                  Where to find the DOT code?
                </button>
              </div>

              {/* Info Panel */}
              {showInfo && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  className="mb-8 p-6 bg-blue-50 border-2 border-blue-200 rounded-xl"
                >
                  <h3 className="font-bold text-blue-900 mb-3">How to Find Your Tyre DOT Code:</h3>
                  <ul className="space-y-2 text-blue-800">
                    <li>• Look on the sidewall of your tyre</li>
                    <li>• Find text starting with "DOT"</li>
                    <li>• The last 4 digits are the manufacturing date</li>
                    <li>• Example: DOT ABCD EFG 1225 means week 12 of 2025</li>
                  </ul>
                </motion.div>
              )}

              {/* Results Section */}
              {result && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="space-y-6"
                >
                  {result.isValid ? (
                    <>
                      {/* Manufacturing Date */}
                      <div className="text-center p-6 bg-gray-50 rounded-xl">
                        <h3 className="text-lg font-bold text-gray-900 mb-2">Manufacturing Date</h3>
                        <div className="text-2xl font-bold text-blue-600">
                          {formatMonth(result.manufacturingDate)}
                        </div>
                        <div className="text-sm text-gray-600 mt-1">
                          Week {result.weekNumber} of {result.year}
                        </div>
                      </div>

                      {/* Expiry Status */}
                      <div className={`text-center p-8 rounded-xl border-4 ${
                        result.isExpired 
                          ? 'bg-red-50 border-red-300' 
                          : 'bg-green-50 border-green-300'
                      }`}>
                        <div className="flex items-center justify-center mb-4">
                          {result.isExpired ? (
                            <AlertTriangle className="text-red-600" size={48} />
                          ) : (
                            <CheckCircle className="text-green-600" size={48} />
                          )}
                        </div>
                        
                        <h3 className={`text-3xl font-bold mb-4 ${
                          result.isExpired ? 'text-red-700' : 'text-green-700'
                        }`}>
                          {result.isExpired ? 'TYRE EXPIRED' : 'TYRE VALID'}
                        </h3>

                        <div className={`text-xl font-semibold mb-2 ${
                          result.isExpired ? 'text-red-600' : 'text-green-600'
                        }`}>
                          Expiry Date: {formatMonth(result.expiryDate)}
                        </div>

                        {result.isExpired ? (
                          <div className="text-red-700">
                            <div className="text-lg font-bold">
                              Expired {Math.abs(result.daysToExpiry)} days ago
                            </div>
                            <div className="text-sm mt-2">
                              Replace immediately for safety
                            </div>
                          </div>
                        ) : (
                          <div className="text-green-700">
                            <div className="text-lg font-bold">
                              {result.daysToExpiry} days remaining
                            </div>
                            <div className="text-sm mt-2">
                              ({Math.floor(result.daysToExpiry / 365)} years, {Math.floor((result.daysToExpiry % 365) / 30)} months)
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Safety Recommendation */}
                      <div className={`p-6 rounded-xl ${
                        result.isExpired 
                          ? 'bg-red-100 border-2 border-red-300' 
                          : result.daysToExpiry < 365 
                            ? 'bg-yellow-100 border-2 border-yellow-300'
                            : 'bg-green-100 border-2 border-green-300'
                      }`}>
                        <h4 className="font-bold mb-2">Safety Recommendation:</h4>
                        {result.isExpired ? (
                          <p className="text-red-800">
                            <strong>REPLACE IMMEDIATELY:</strong> Expired tyres pose serious safety risks including blowouts, 
                            reduced grip, and compromised handling. Do not drive on expired tyres.
                          </p>
                        ) : result.daysToExpiry < 365 ? (
                          <p className="text-yellow-800">
                            <strong>PLAN REPLACEMENT:</strong> Your tyres will expire within a year. Start planning for 
                            replacement and monitor them closely for signs of aging.
                          </p>
                        ) : (
                          <p className="text-green-800">
                            <strong>TYRES OKAY:</strong> Your tyres are still within the safe age limit. Continue regular 
                            inspections and maintain proper pressure.
                          </p>
                        )}
                      </div>
                    </>
                  ) : (
                    <div className="text-center p-8 bg-red-50 border-4 border-red-300 rounded-xl">
                      <AlertTriangle className="text-red-600 mx-auto mb-4" size={48} />
                      <h3 className="text-2xl font-bold text-red-700 mb-2">Invalid DOT Code</h3>
                      <p className="text-red-600">{result.errorMessage}</p>
                    </div>
                  )}
                </motion.div>
              )}
            </div>
          </motion.div>

          {/* SEO Content Section */}
          <CalculatorQuoteRequest
            toolId="tyre-expiry-date-calculator"
            title="Snap your tyre size and request multiple replacement quotes"
            description="Take clear photos of your tyre size and DOT code, then request multiple quotes from different tyre providers. Include your vehicle details and tyre quantity, choose your location, and attach the photos in the upload step."
          />

          <div className="max-w-6xl mx-auto">
            <div className="bg-white rounded-2xl p-8 shadow-xl">
              <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
                Tyre Expiry and Road Safety: What You Need to Know
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <Calendar className="text-blue-600" />
                    Understanding Tyre Expiry Dates
                  </h3>
                  <p className="text-gray-700 mb-4">
                    Tyres have a manufacturing date encoded in the DOT (Department of Transportation) code found on the sidewall. 
                    This 4-digit code represents the week and year of manufacture. The first two digits indicate the week 
                    (01-53), and the last two digits represent the year.
                  </p>
                  <p className="text-gray-700">
                    Regardless of tread depth or visual condition, tyres should be replaced after 5-6 years from the 
                    manufacturing date due to rubber degradation, which can lead to sudden failures and safety risks.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <Shield className="text-green-600" />
                    Why Tyre Age Matters for Safety
                  </h3>
                  <p className="text-gray-700 mb-4">
                    Studies of tyre safety show that maintaining proper tyre pressure, observing tyre and vehicle load limits, 
                    and replacing aged tyres are critical for road safety. Even tyres with good tread depth can become 
                    dangerous as the rubber compounds deteriorate over time.
                  </p>
                  <p className="text-gray-700">
                    Environmental factors such as heat, UV exposure, and ozone accelerate rubber aging, making regular 
                    age checks essential for vehicle safety.
                  </p>
                </div>
              </div>

              <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-6 mb-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">
                  How to Find and Read Your Tyre DOT Code
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="text-center">
                    <div className="w-16 h-16 bg-blue-500 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Search className="text-white" size={24} />
                    </div>
                    <h4 className="font-bold text-gray-900 mb-2">1. Locate the DOT Code</h4>
                    <p className="text-gray-700 text-sm">
                      Look on the sidewall of your tyre for text beginning with "DOT". The code may be on either 
                      the inner or outer sidewall.
                    </p>
                  </div>

                  <div className="text-center">
                    <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Target className="text-white" size={24} />
                    </div>
                    <h4 className="font-bold text-gray-900 mb-2">2. Find the Date Code</h4>
                    <p className="text-gray-700 text-sm">
                      The manufacturing date is the last 4 digits of the DOT code. For example, "1225" means 
                      the 12th week of 2025.
                    </p>
                  </div>

                  <div className="text-center">
                    <div className="w-16 h-16 bg-purple-500 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Calendar className="text-white" size={24} />
                    </div>
                    <h4 className="font-bold text-gray-900 mb-2">3. Calculate Age</h4>
                    <p className="text-gray-700 text-sm">
                      Use our calculator above to determine the exact manufacturing date and whether your 
                      tyres have exceeded the 5-year safety limit.
                    </p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Signs of Tyre Aging</h3>
                  <ul className="space-y-3 text-gray-700">
                    <li className="flex items-start gap-3">
                      <AlertTriangle className="text-orange-500 mt-1" size={16} />
                      <span><strong>Cracking:</strong> Small cracks in the sidewall or tread area</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <AlertTriangle className="text-orange-500 mt-1" size={16} />
                      <span><strong>Hardening:</strong> Rubber feels hard and inflexible</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <AlertTriangle className="text-orange-500 mt-1" size={16} />
                      <span><strong>Discoloration:</strong> Unusual color changes or fading</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <AlertTriangle className="text-orange-500 mt-1" size={16} />
                      <span><strong>Vibration:</strong> Unusual vibration during driving</span>
                    </li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Tyre Safety Best Practices</h3>
                  <ul className="space-y-3 text-gray-700">
                    <li className="flex items-start gap-3">
                      <CheckCircle className="text-green-500 mt-1" size={16} />
                      <span>Check tyre age every 6 months after 3 years</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle className="text-green-500 mt-1" size={16} />
                      <span>Maintain proper tyre pressure at all times</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle className="text-green-500 mt-1" size={16} />
                      <span>Rotate tyres regularly for even wear</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle className="text-green-500 mt-1" size={16} />
                      <span>Replace tyres in sets for optimal performance</span>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="bg-gray-800 text-white rounded-xl p-8 text-center">
                <h3 className="text-2xl font-bold mb-4">Professional Tyre Services</h3>
                <p className="text-gray-300 mb-6 max-w-3xl mx-auto">
                  Need new tyres or professional inspection? Our network of certified tyre experts can help you 
                  find the right tyres for your vehicle and ensure proper installation for maximum safety and performance.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <button
                    onClick={handleTyreQuoteRequest}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-lg font-semibold transition-colors flex items-center gap-2 justify-center"
                  >
                    <Car size={20} />
                    Find Tyre Services
                  </button>
                  <button 
                    onClick={handleTyreQuoteRequest}
                    className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-gray-800 px-8 py-3 rounded-lg font-semibold transition-colors flex items-center gap-2 justify-center"
                  >
                    <Gauge size={20} />
                    Book Inspection
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* FAQ Section */}
          <div className="max-w-6xl mx-auto mt-12">
            <div className="bg-white rounded-2xl p-8 shadow-xl">
              <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
                Frequently Asked Questions About Tyre Expiry in South Africa
              </h2>

              <div className="space-y-6">
                {/* FAQ 1 */}
                <div className="border-b border-gray-200 pb-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
                    <Info className="text-blue-600" size={20} />
                    What is the expiry date on tyres in South Africa?
                  </h3>
                  <p className="text-gray-700">
                    In South Africa, tyres should be replaced after <strong>5-6 years from the manufacturing date</strong>, 
                    regardless of tread depth. The manufacturing date is found in the DOT code on the sidewall. 
                    While there's no specific legal expiry date, industry standards and safety experts recommend 
                    replacement after 5 years for optimal safety.
                  </p>
                </div>

                {/* FAQ 2 */}
                <div className="border-b border-gray-200 pb-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
                    <Search className="text-blue-600" size={20} />
                    How do I check my tyre expiry date?
                  </h3>
                  <p className="text-gray-700">
                    To check your tyre expiry date: <strong>1)</strong> Find the DOT code on your tyre sidewall, 
                    <strong>2)</strong> Look for the last 4 digits (e.g., "1225"), <strong>3)</strong> The first 
                    two digits are the week (12) and last two are the year (25). Use our calculator above to 
                    determine the exact manufacturing and expiry dates.
                  </p>
                </div>

                {/* FAQ 3 */}
                <div className="border-b border-gray-200 pb-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
                    <AlertTriangle className="text-red-600" size={20} />
                    Are 7 year old tyres too old?
                  </h3>
                  <p className="text-gray-700">
                    <strong>Yes, 7-year-old tyres are definitely too old and unsafe to use.</strong> Tyres older 
                    than 6 years pose serious safety risks due to rubber degradation, even if they look good. 
                    The rubber compounds break down over time, leading to reduced grip, potential blowouts, 
                    and compromised handling. Replace them immediately.
                  </p>
                </div>

                {/* FAQ 4 */}
                <div className="border-b border-gray-200 pb-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
                    <Calendar className="text-blue-600" size={20} />
                    When to replace tyres in South Africa?
                  </h3>
                  <p className="text-gray-700">
                    Replace tyres in South Africa when: <strong>1)</strong> Tread depth reaches 1.6mm (legal limit), 
                    <strong>2)</strong> Tyres are 5-6 years old from manufacturing date, <strong>3)</strong> Visible 
                    sidewall cracks or bulges appear, <strong>4)</strong> Uneven wear patterns develop, or 
                    <strong>5)</strong> After any puncture damage to the sidewall.
                  </p>
                </div>

                {/* FAQ 5 */}
                <div className="border-b border-gray-200 pb-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
                    <Shield className="text-red-600" size={20} />
                    Is it okay to use an expired tyre?
                  </h3>
                  <p className="text-gray-700">
                    <strong>No, it is not safe to use expired tyres.</strong> Expired tyres have degraded rubber 
                    compounds that can cause sudden failures, reduced braking performance, poor wet weather grip, 
                    and increased risk of blowouts. Using expired tyres puts you, your passengers, and other 
                    road users at serious risk.
                  </p>
                </div>

                {/* FAQ 6 */}
                <div className="border-b border-gray-200 pb-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
                    <Gauge className="text-blue-600" size={20} />
                    How old can tyres be legally?
                  </h3>
                  <p className="text-gray-700">
                    In South Africa, there's no specific legal age limit for tyres, but they must meet the 
                    <strong>minimum tread depth of 1.6mm</strong>. However, safety regulations require tyres 
                    to be roadworthy. Most insurance companies and safety experts recommend replacement after 
                    5-6 years regardless of tread depth.
                  </p>
                </div>

                {/* FAQ 7 */}
                <div className="border-b border-gray-200 pb-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
                    <Clock className="text-blue-600" size={20} />
                    Do tires expire if not used?
                  </h3>
                  <p className="text-gray-700">
                    <strong>Yes, tyres expire even if not used.</strong> Rubber degrades over time due to oxidation, 
                    UV exposure, temperature changes, and ozone. Unused tyres stored properly may last slightly 
                    longer, but should still be replaced after 6-10 years maximum. Heat and poor storage 
                    conditions accelerate aging.
                  </p>
                </div>

                {/* FAQ 8 */}
                <div className="border-b border-gray-200 pb-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
                    <TrendingUp className="text-blue-600" size={20} />
                    How to tell if tires are expired?
                  </h3>
                  <p className="text-gray-700">
                    Check if tyres are expired by: <strong>1)</strong> Reading the DOT code for manufacturing date, 
                    <strong>2)</strong> Looking for sidewall cracks or crazing, <strong>3)</strong> Checking for 
                    rubber hardening, <strong>4)</strong> Noting any unusual vibrations while driving, 
                    <strong>5)</strong> Observing discoloration or weathering signs.
                  </p>
                </div>

                {/* FAQ 9 */}
                <div className="pb-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
                    <Target className="text-blue-600" size={20} />
                    What is the life of a tyre in KM?
                  </h3>
                  <p className="text-gray-700">
                    Life span of a tyre in KM is 60,000-80,000km. Tyre life varies by type and usage: <strong>Budget tyres:</strong> 40,000-60,000km, 
                    <strong>Mid-range tyres:</strong> 60,000-80,000km, <strong>Premium tyres:</strong> 
                    80,000-100,000km+. However, <strong>age limits override mileage</strong> - replace tyres 
                    after 5-6 years regardless of kilometers driven. Driving habits, road conditions, and 
                    maintenance significantly affect tyre life.
                  </p>
                </div>
              </div>

              <div className="mt-8 p-6 bg-blue-50 rounded-xl border-2 border-blue-200">
                <h4 className="text-lg font-bold text-blue-900 mb-3">Need Professional Tyre Assessment?</h4>
                <p className="text-blue-800 mb-4">
                  If you're unsure about your tyre condition or need expert advice, submit your tyre picture on the website or mobile app and a certified consultant will get back to you with a free assessment.
                  Always consult with certified tyre professionals who can assess your specific situation and recommend the best course of action.
                </p>
                <button
                  onClick={handleTyreQuoteRequest}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
                >
                  Find Local Tyre Experts
                </button>
              </div>
            </div>
          </div>    
        </div>
        
      </div>
      
<DownloadApp />
      {/* Request Item Form Popup */}
       {(
        (isPopupOpen && popupType === 'carousel') || 
        localShowPopup
      ) && (
        <RequestItemFormPopup 
          currentPopupType={popupType || 'carousel'}
          isContextOpen={isPopupOpen || localShowPopup}
          onClose={handleClosePopup}
          initialData={getInitialData()}
        />
      )}
      
    </>
    
  );
};

export default TyreExpiryDateCalculator;